import { useContext, useEffect, useMemo, useRef } from 'react';

import HeroJuneThematicContent from './HeroJuneThematicContent';

import ClientContext from '@/contexts/client.context';

import { useBetterMediaQuery } from '@swordhealth/ui-corporate';
import { sword } from '@swordhealth/ui-corporate/themes';
import getImgSrcWorkaround from '@/utils/images';

import {
  Inner,
  Wrapper,
  MotionVideoContainer,
  Overlay,
  WrappedSection,
  WrappedContainer,
} from './styles';

const videoAnimationProps = {
  animate: { opacity: 1, y: 0 },
  autoPlay: false,
  initial: { opacity: 0, y: 75 },
  loop: false,
  muted: true,
  playsInline: true,
  transition: { duration: 0.8, ease: 'easeInOut' },
};

const HeroJuneThematic = ({ config, content, userName }) => {
  const { openModal, client } = useContext(ClientContext);
  const { media, ...contentRest } = content?.heroContent ?? {};
  const videoRef = useRef(null);

  const isTabletOrMobile = useBetterMediaQuery(`(max-width: ${sword.breakpoints.maxMd})`);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.defaultMuted = true;
    const timer = setTimeout(() => {
      video.play().catch(() => {});
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const memoizedHeroWithOverlay = useMemo(() => {
    const videoUrl = getImgSrcWorkaround(media?.data?.attributes?.url);
    if (!videoUrl) return null;

    return (
      <WrappedContainer>
        <Inner>
          <HeroJuneThematicContent
            client={client}
            openModal={openModal}
            userName={userName}
            {...contentRest}
          />
          <MotionVideoContainer ref={videoRef} {...videoAnimationProps}>
            <source src={getImgSrcWorkaround(media?.data?.attributes?.url)} />
          </MotionVideoContainer>
        </Inner>
      </WrappedContainer>
    );
  }, [client, contentRest, media?.data?.attributes?.url, openModal, userName]);

  return (
    <WrappedSection spaceBottom={config?.spaceBottom}>
      <Overlay $angle={isTabletOrMobile ? 180 : 90} />
      <Wrapper>{memoizedHeroWithOverlay}</Wrapper>
    </WrappedSection>
  );
};

HeroJuneThematic.defaultConfig = {
  colored: false,
  marginBottom: true,
  rounded: true,
  spaceBottom: false,
  spaceTop: false,
  withGradient: false,
};

HeroJuneThematic.lockedConfig = {
  rounded: false,
  withGradient: false,
};

export default HeroJuneThematic;
